import { useCallback, useEffect, useState } from 'react';

export interface UseMicrophonePermissionReturn {
    canUseMicrophone: boolean;
    requestPermission: () => Promise<boolean>;
    permissionState: 'prompt' | 'granted' | 'denied' | 'unknown';
}

const useMicrophonePermission = (): UseMicrophonePermissionReturn => {
    const [permissionState, setPermissionState] = useState<'prompt' | 'granted' | 'denied' | 'unknown'>('unknown');

    useEffect(() => {
        // Check if API exists
        if (!navigator?.mediaDevices?.getUserMedia) {
            setPermissionState('denied');
            return;
        }

        // Try to check permission status (not supported in iOS Safari)
        const checkPermissionStatus = async () => {
            try {
                // This API is not fully supported in iOS Safari
                if (navigator.permissions && navigator.permissions.query) {
                    const permissionStatus = await navigator.permissions.query({ name: 'microphone' as PermissionName });
                    setPermissionState(permissionStatus.state as 'prompt' | 'granted' | 'denied');

                    // Listen for permission changes
                    permissionStatus.onchange = () => {
                        setPermissionState(permissionStatus.state as 'prompt' | 'granted' | 'denied');
                    };
                } else {
                    // iOS Safari - we don't know the status until we try
                    // Show the button and request permission on click
                    setPermissionState('prompt');
                }
            } catch (error) {
                // Fallback for browsers that don't support permissions API
                console.log('Permissions API not supported:', error);
                setPermissionState('prompt');
            }
        };

        checkPermissionStatus();
    }, []);

    // Request permission - MUST be called from a user gesture (click event)
    const requestPermission = useCallback(async (): Promise<boolean> => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

            // Permission granted - stop the stream immediately
            stream.getTracks().forEach((track) => track.stop());

            setPermissionState('granted');
            return true;
        } catch (error) {
            // Permission denied or error
            console.log('Microphone permission denied:', error);

            // Check error type
            if (error instanceof DOMException) {
                if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                    setPermissionState('denied');
                } else {
                    setPermissionState('unknown');
                }
            } else {
                setPermissionState('unknown');
            }

            return false;
        }
    }, []);

    // Show microphone button if:
    // 1. API is available
    // 2. Permission is granted, OR
    // 3. We haven't asked yet (prompt/unknown) - especially important for iOS
    const hasAPI = typeof window !== 'undefined' && navigator?.mediaDevices?.getUserMedia !== undefined;
    const canUseMicrophone = permissionState !== 'denied' && hasAPI;

    return {
        canUseMicrophone,
        requestPermission,
        permissionState
    };
};

export default useMicrophonePermission;

